import { AlertCircle, Check, CircleDot } from "lucide-react";

/**
 * TrafficLightBadge — fleet-wide traffic-light indicator (red / yellow / green).
 * Used by partner rating cards, pricing bands, and any other place that needs
 * a tone + short label chip.
 *
 * Public /partners uses the "quiet" variant — a soft "Curated" chip in the
 * appropriate tone — because the Partner Trust Standard keeps the raw
 * Red/Yellow/Green language internal. Admin uses "verbose" to show the full
 * rating label.
 */
const TONE_MAP = {
    green: {
        label: "Green",
        hint: "Highly recommended",
        quietLabel: "Curated",
        className: "text-emerald-300 border-emerald-500/50 bg-emerald-900/30",
        iconColor: "text-emerald-400",
    },
    yellow: {
        label: "Yellow",
        hint: "Worth considering",
        quietLabel: "Curated",
        className: "text-amber-200 border-amber-400/50 bg-amber-900/30",
        iconColor: "text-amber-400",
    },
    red: {
        label: "Red",
        hint: "Low priority",
        quietLabel: "Under review",
        className: "text-rose-200 border-rose-400/50 bg-rose-900/40",
        iconColor: "text-rose-400",
    },
};

export default function TrafficLightBadge({
    rating,
    variant = "verbose", // "verbose" | "quiet"
    size = "md",         // "sm" | "md"
    testId,
}) {
    const r = rating in TONE_MAP ? rating : "yellow";
    const t = TONE_MAP[r];
    const sizeClasses =
        size === "sm"
            ? "text-[0.5rem] tracking-[0.2em] px-2 py-0.5"
            : "text-[0.6rem] tracking-[0.3em] px-2.5 py-1";
    const label = variant === "quiet" ? t.quietLabel : t.label;
    const Icon = variant === "quiet" ? Check : CircleDot;
    return (
        <span
            className={`inline-flex items-center gap-1.5 rounded-full border uppercase font-semibold ${sizeClasses} ${t.className}`}
            data-testid={testId || `trafficlight-${r}`}
            data-rating={r}
            data-variant={variant}
            title={t.hint}
        >
            <Icon size={size === "sm" ? 9 : 11} className={t.iconColor} />
            {label}
        </span>
    );
}

export function TrafficLightDot({ rating, size = 10 }) {
    const r = rating in TONE_MAP ? rating : "yellow";
    const t = TONE_MAP[r];
    return (
        <span
            className={`inline-block rounded-full ${t.iconColor}`}
            style={{ width: size, height: size, background: "currentColor" }}
            data-testid={`trafficlight-dot-${r}`}
            aria-label={t.label}
        />
    );
}

export { TONE_MAP };
